import { combineReducers } from 'redux'
import { categories, CategoriesPayload } from './products/categories'
import company, { CompanyPayload } from './globals/company'
import countries, { CountryJson } from './globals/countries'
import currentPage, { CurrentPagePayload } from './globals/currentPage'
import customerGroups, { CustomerGroupPayload } from './customers/customerGroups'
import features, { FeaturesPayload } from './products/features'
import fingerprint, { FingerprintPayload } from './globals/fingerprint'
import user, { UserPayload } from './globals/user'
import config, { ConfigPayload } from './globals/config'
import priceZoneGroups, { PriceZoneGroupsPayload } from './globals/priceZoneGroups'
import productTypes, { ProductTypesPayload } from './products/productTypes'
import suppliers, { SupplierPayload } from './products/suppliers'
import specificPriceRules, { SpecificPriceRulesPayload } from './products/specificPriceRules'
import warehouses, { WarehousePayload } from './company/warehouses'
import popupMessage, { PopupMessagePayload } from './globals/popupMessage'
import groupVats, { GroupVatJson } from './globals/groupVats'
import antiUnsave, { AntiUnsavePayload } from './globals/antiUnsave'
import workingCenterTypes, { WorkingCenterTypePayload } from './company/workingCenterTypes'
import brands, { BrandsPayload } from './products/brands'
import collections, { CollectionsPayload } from './products/collections'
import variants, { VariantsPayload } from './products/variants'
import workingCenters, { WorkingCenterPayload } from './company/workingCenters'
import { configureStore } from '@reduxjs/toolkit'
import billingGroups, { BillingGroupPayload } from './company/billingGroups'
import billingRetentions, { BillingRetentionPayload } from './company/billingRetentions'
import configChargingMethods, { ConfigChargingMethodPayload } from './company/configChargingMethods'
import customers, { CustomerPayload } from './customers/customer'
import shippingCompanies, { ShippingCompanyPayload } from './company/shippingCompanies'
import pdfTemplates, { PdfTemplatePayload } from './company/pdfTemplates'
import shippingServices, { ShippingServicePayload } from './company/shippingServices'
import { createContext } from 'react'
import groups, { GroupPayload } from './groups/groups'
import employees, { EmployeesPayload } from './company/employees'
import lastNotification, { LastNotificationPayload } from './company/lastNotification'
import employeeAbsenceTypes, { EmployeeAbsenceTypePayload } from './company/employeeAbsenceTypes'
import taskCFields, { TaskCFieldPayload } from './company/taskCFields'
import taskTypes, { TaskTypePayload } from './company/taskTypes'
import customerProjectTypes, { CustomerProjectTypePayload } from './customers/customerProjectTypes'
import taskHelpers, { TaskHelpersPayload } from './company/taskHelpers'
import bottomNavApp, { BottomNavAppPayload } from './globals/bottomNavApp'
import restrictTimeControlAccess, { ChangeRestrictTimeControlAccessPayload } from './globals/restrictTimeControlAccess'
import { PdfQuoteOrderTemplatePayload } from './company/pdfQuoteOrderTemplates'
import pdfQuoteOrderTemplates from './company/pdfQuoteOrderTemplates'
import sellTags, { SellTagsPayload } from './sells/sellTags'
import costsSell, { CostSellPayload } from './company/costsSell'
import costsBuy, { CostBuyPayload } from './company/costsBuy'
import csvOrderTemplates, { CsvOrderTemplatePayload } from './company/csvOrderTemplates'
import buyTags, { BuyTagsPayload } from './buys/buyTags'
import sellVariables, { SellVariablePayload } from './company/sellVariables'
import workSheetGroupStatuses, { WorkSheetGroupStatusesPayload } from './sells/workSheetGroupStatus'
import labels, { LabelsPayload } from './company/labels'
import paymentTypes, { PaymentTypesPayload } from './payments/paymentTypes'
import paymentMethods, { PaymentMethodsPayload } from './payments/paymentMethods'
import tChecklists, { TChecklistsPayload } from './customers/tChecklists'

export interface AllReduxPayloads {
    billingGroups: BillingGroupPayload
    billingRetentions: BillingRetentionPayload
    brands: BrandsPayload
    buyTags: BuyTagsPayload
    categories: CategoriesPayload
    collections: CollectionsPayload
    configChargingMethods: ConfigChargingMethodPayload
    costsBuy: CostBuyPayload
    costsSell: CostSellPayload
    csvOrderTemplates: CsvOrderTemplatePayload
    customers: CustomerPayload
    customerGroups: CustomerGroupPayload
    customerProjectTypes: CustomerProjectTypePayload
    tChecklists: TChecklistsPayload
    employees: EmployeesPayload
    employeeAbsenceTypes: EmployeeAbsenceTypePayload
    features: FeaturesPayload
    labels: LabelsPayload
    groupVats: GroupVatJson
    groups: GroupPayload
    priceZoneGroups: PriceZoneGroupsPayload
    productTypes: ProductTypesPayload
    sellTags: SellTagsPayload
    sellVariables: SellVariablePayload
    shippingCompanies: ShippingCompanyPayload
    shippingServices: ShippingServicePayload
    specificPriceRules: SpecificPriceRulesPayload
    suppliers: SupplierPayload
    taskCFields: TaskCFieldPayload
    taskHelpers: TaskHelpersPayload
    taskTypes: TaskTypePayload
    variants: VariantsPayload
    warehouses: WarehousePayload
    workingCenters: WorkingCenterPayload
    workingCenterTypes: WorkingCenterTypePayload
    workSheetGroupStatuses: WorkSheetGroupStatusesPayload
    paymentTypes: PaymentTypesPayload
    paymentMethods: PaymentMethodsPayload
    pdfQuoteOrderTemplates: PdfQuoteOrderTemplatePayload
    pdfTemplates: PdfTemplatePayload
}

const allReducers = combineReducers({
    billingGroups,
    billingRetentions,
    brands,
    buyTags,
    categories,
    collections,
    configChargingMethods,
    costsBuy,
    costsSell,
    csvOrderTemplates,
    customers,
    customerProjectTypes,
    tChecklists,
    employees,
    employeeAbsenceTypes,
    customerGroups,
    features,
    labels,
    groupVats,
    groups,
    priceZoneGroups,
    productTypes,
    sellTags,
    sellVariables,
    shippingCompanies, 
    shippingServices, 
    specificPriceRules,
    suppliers,
    taskCFields,
    taskHelpers,
    taskTypes,
    variants,
    warehouses,
    workingCenters,
    workingCenterTypes,
    workSheetGroupStatuses,
    paymentTypes,
    paymentMethods,
    pdfQuoteOrderTemplates,
    pdfTemplates,
})

const rootReducer = (state: any, action: any) => {
    if (action.type === 'RESET_APP') {
        state = undefined;
    }
    return allReducers(state, action);
}


const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
        immutableCheck: false,
    })
})

export interface AllReduxAppPayloads {
    antiUnsave: AntiUnsavePayload
    bottomNavApp: BottomNavAppPayload
    company: CompanyPayload
    config: ConfigPayload
    countries: CountryJson
    currentPage: CurrentPagePayload
    popupMessage: PopupMessagePayload
    restrictTimeControlAccess: ChangeRestrictTimeControlAccessPayload
    fingerprint: FingerprintPayload
    user: UserPayload
    lastNotification: LastNotificationPayload
}

const appReducer = combineReducers({
    antiUnsave,
    bottomNavApp, 
    company,
    config,
    countries,
    currentPage,
    popupMessage,
    restrictTimeControlAccess,
    fingerprint,
    user,
    lastNotification
})

export const appStore = configureStore({ reducer: appReducer })


export const GlobalContext = createContext<any>(null)

export default store